import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

import clsx from 'clsx';

// Component
import H2 from '@/components/base/Headings/H2';

const SIZE_DEFAULT = 'DEFAULT';
const SIZE_LG = 'lg';
const SIZE_XL = 'xl';
const SIZE_3XL = '3xl';
const SIZE_5XL = '5xl';
const SIZE_NAV_FIT = 'nav_fit';

const ModalSize = {
    [SIZE_DEFAULT]: 'sm:max-w-[37.5rem] my-8 rounded-md',
    [SIZE_LG]: 'sm:max-w-lg my-8 rounded-md',
    [SIZE_XL]: 'sm:max-w-2xl my-8 rounded-md',
    [SIZE_3XL]: 'sm:max-w-3xl my-8 rounded-md',
    [SIZE_5XL]: 'sm:max-w-5xl my-8 rounded-md',
    [SIZE_NAV_FIT]: 'md:max-w-lg lg:max-w-3xl xl:max-w-5xl mt-28 mb-8 md:mt-8 rounded-md'
} as const;

const OVERLAY_LIGHT = 'OVERLAY_LIGHT';
const OVERLAY_DARK = 'OVERLAY_DARK';

type ModalBlockContentProps = {
    children: ReactNode;
    variantOverlay?: typeof OVERLAY_LIGHT | typeof OVERLAY_DARK;
    size?: keyof typeof ModalSize;
    alignContent?:
        | 'items-center'
        | 'items-start'
        | 'items-end'
        | 'items-baseline'
        | 'items-stretch';
};
export default function ModalBlockContent({
    children,
    variantOverlay = OVERLAY_LIGHT,
    size = SIZE_DEFAULT,
    alignContent = 'items-center'
}: ModalBlockContentProps) {
    return (
        <div className="absolute inset-0 z-10 overflow-y-auto overflow-x-hidden">
            <div
                className={clsx(
                    'relative flex min-h-full justify-center px-3 text-center',
                    alignContent
                )}>
                <div
                    className={clsx('absolute inset-0 backdrop-blur-md', {
                        'bg-white/20 firefox:bg-white firefox:bg-opacity-90 dark:bg-icoBlack-400/50 dark:firefox:bg-icoBlack dark:firefox:bg-opacity-70':
                            variantOverlay === OVERLAY_LIGHT,
                        'bg-icoBlack-400/50 backdrop-blur-md firefox:bg-icoBlack/70':
                            variantOverlay === OVERLAY_DARK
                    })}
                />

                {/* This element is to trick the browser into centering the modal contents. */}
                <div
                    className={clsx(
                        'relative inline-block w-full overflow-hidden border border-icoGray-300 bg-white text-left align-middle transition-all dark:border-icoDarkMode-wolf dark:bg-icoDarkMode-anthracite',
                        ModalSize[size]
                    )}>
                    {children}
                </div>
            </div>
        </div>
    );
}

const DefaultBodyStyle = 'px-5 sm:px-12 pt-12';
type BodyProps = ComponentPropsWithoutRef<'div'>;
const Body = ({ className, ...restProps }: BodyProps) => {
    return <div className={className ?? DefaultBodyStyle} {...restProps} />;
};

ModalBlockContent.Body = Body;

//Modal Title 👇
const DefaultTitleStyle = 'text-center';
type TitleProps = ComponentPropsWithoutRef<'h2'>;
const Title = ({ className, children, ...restProps }: TitleProps) => (
    <H2 className={className ?? DefaultTitleStyle} {...restProps}>
        {children}
    </H2>
);

ModalBlockContent.Title = Title;

// Modal Content 👇
const DefaultContentStyle = 'py-m';
type ContentProps = ComponentPropsWithoutRef<'div'>;
const Content = ({ className, ...restProps }: ContentProps) => (
    <div className={className ?? DefaultContentStyle} {...restProps} />
);

ModalBlockContent.Content = Content;

// Modal Footer 👇
const DefaultFooterStyle = 'flex items-center justify-between px-5 sm:px-12 pb-12';
type FooterProps = ComponentPropsWithoutRef<'div'> & {
    as?: ElementType;
    isRow: boolean;
    divider: boolean;
};
const Footer = ({
    className,
    as: Component = 'div',
    isRow = true,
    divider = false,
    ...restProps
}: FooterProps) => {
    return (
        <>
            {divider ? <hr className="border-t-1 mt-12 border-icoGray-300" /> : null}
            <Component
                className={
                    className ??
                    clsx(DefaultFooterStyle, {
                        'flex-col space-y-m': !isRow,
                        'pt-m': divider,
                        'pt-8': !divider
                    })
                }
                {...restProps}
            />
        </>
    );
};

ModalBlockContent.Footer = Footer;
